import type { Chart } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import type { ChartSettingsSnapshot, IndicatorTemplateData, SerializedOverlay } from '@/types';

/** Active indicators as `{name, params?, colors?}` — RS_LINE is tracked via rsLineConfigs instead. */
export function buildCurrentIndicators(): IndicatorTemplateData['indicators'] {
  const { indicatorPaneMap, indicatorParams, indicatorColors } = useChartStore.getState();
  return [...indicatorPaneMap.keys()]
    .filter((name) => name !== 'RS_LINE')
    .map((name) => ({
      name,
      ...(indicatorParams[name]?.length ? { params: indicatorParams[name] } : {}),
      ...(indicatorColors[name]?.length ? { colors: indicatorColors[name] } : {}),
    }));
}

/** Live candle type — the one piece of the snapshot that isn't plain store state (HLC is an indicator). */
export function buildCurrentCandleType(chart: Chart | null): string | undefined {
  if (!chart) return undefined;
  if (chart.getIndicatorByPaneId('candle_pane', 'HLC')) return 'hlc';
  return chart.getStyles()?.candle?.type ?? undefined;
}

// Open-ended tools (totalStep = MAX_SAFE_INTEGER) only finish via a user gesture;
// re-created from saved points they'd stay stuck "mid-draw" and swallow the next click.
const UNSHAREABLE_OVERLAYS = new Set(['anyWaves', 'curve']);

function serializeDrawings(chart: Chart): SerializedOverlay[] {
  const { drawingOverlays, indicatorPaneMap, symbol } = useChartStore.getState();
  const out: SerializedOverlay[] = [];

  for (const { id, symbol: drawnOn } of drawingOverlays) {
    // Drawings survive symbol switches on the live chart, but only this
    // symbol's drawings belong to this analysis.
    if (drawnOn !== symbol) continue;
    const overlay = chart.getOverlayById(id);
    if (!overlay) continue; // removed since it was drawn
    if (UNSHAREABLE_OVERLAYS.has(overlay.name)) continue;

    // Sub-pane ids are generated per session, so key them by owning indicator.
    let paneIndicator: string | undefined;
    if (overlay.paneId !== 'candle_pane') {
      paneIndicator = [...indicatorPaneMap].find(([, paneId]) => paneId === overlay.paneId)?.[0];
      if (!paneIndicator) continue; // pane we can't re-create on the other side
    }

    out.push({
      name: overlay.name,
      paneId: overlay.paneId,
      ...(paneIndicator ? { paneIndicator } : {}),
      points: overlay.points.map((p) =>
        typeof p.timestamp === 'number'
          ? { timestamp: p.timestamp, value: p.value }
          : { dataIndex: p.dataIndex, value: p.value },
      ),
      mode: overlay.mode,
      lock: overlay.lock,
      visible: overlay.visible,
      ...(overlay.styles ? { styles: overlay.styles as Record<string, unknown> } : {}),
      ...(overlay.extendData !== undefined && overlay.extendData !== null ? { extendData: overlay.extendData } : {}),
    });
  }
  return out;
}

/** Full snapshot of the current chart (indicators, settings, optionally drawings). */
export function buildCurrentTemplateSnapshot(
  chart: Chart | null,
  opts: { includeDrawings?: boolean } = {},
): IndicatorTemplateData {
  const s = useChartStore.getState();

  const chartSettings: ChartSettingsSnapshot = {
    candleType: buildCurrentCandleType(chart) ?? s.activeCandleType,
    candleUpColor: s.candleUpColor,
    candleDownColor: s.candleDownColor,
    hlcUpColor: s.hlcUpColor,
    hlcDownColor: s.hlcDownColor,
    showHGrid: s.showHGrid,
    showVGrid: s.showVGrid,
    showPriceMarkHigh: s.showPriceMarkHigh,
    showPriceMarkLow: s.showPriceMarkLow,
    showLastPriceLine: s.showLastPriceLine,
    showCrosshair: s.showCrosshair,
    yAxisReverse: s.yAxisReverse,
    priceScaleType: s.priceScaleType,
  };

  const scanConfigs = Object.keys(s.indicatorScanConfigs).length ? s.indicatorScanConfigs : undefined;
  const rsLineConfigs = s.rsLineConfigs.length ? s.rsLineConfigs : undefined;
  const drawings = opts.includeDrawings && chart ? serializeDrawings(chart) : [];

  return {
    version: 1,
    indicators: buildCurrentIndicators(),
    volumeLabelEnabled: s.volumeLabelEnabled,
    ...(scanConfigs ? { scanConfigs } : {}),
    ...(rsLineConfigs ? { rsLineConfigs } : {}),
    chartSettings,
    epsLineEnabled: s.epsLineEnabled,
    epsLineAlignToAnnouncement: s.epsLineAlignToAnnouncement,
    epsLineAxisScale: s.epsLineAxisScale,
    epsLineForwardModel: s.epsLineForwardModel,
    epsLineShowForwardProjection: s.epsLineShowForwardProjection,
    foreignHoldingUpColor: s.foreignHoldingUpColor,
    foreignHoldingDownColor: s.foreignHoldingDownColor,
    foreignHoldingFlatColor: s.foreignHoldingFlatColor,
    foreignHoldingMA1Period: s.foreignHoldingMA1Period,
    foreignHoldingMA1Color: s.foreignHoldingMA1Color,
    foreignHoldingMA2Period: s.foreignHoldingMA2Period,
    foreignHoldingMA2Color: s.foreignHoldingMA2Color,
    foreignHoldingConfirmBullColor: s.foreignHoldingConfirmBullColor,
    foreignHoldingDistributionColor: s.foreignHoldingDistributionColor,
    foreignHoldingAccumulationColor: s.foreignHoldingAccumulationColor,
    foreignHoldingConfirmBearColor: s.foreignHoldingConfirmBearColor,
    cupHandleEnabled: s.cupHandleEnabled,
    cupHandleSettings: s.cupHandleSettings,
    williamOneilSellEnabled: s.williamOneilSellEnabled,
    williamOneilSellSettings: s.williamOneilSellSettings,
    ...(drawings.length ? { drawings } : {}),
  };
}
